import type { QueryClient, QueryKey } from "@tanstack/react-query";
import type { DateTime } from "luxon";
import type { ScheduleDay, ScheduleWeek } from "../utils/models";
import { toIsoDate, weekEndOf } from "../utils/scheduleWeeks";
import { scheduleKeys, scheduleWagesKeys, staffShiftStatsKeys } from "./scheduleCache";

export type ScheduleInvalidation = { dates: DateTime[] } | { allWeeks: true };

// The week's first date is the key's last part; the week runs through six days after it.
const weekHoldsAnyOf = (queryKey: QueryKey, dates: string[]): boolean => {
	const weekStart = queryKey[2];
	return typeof weekStart === "string" && dates.some((date) => date >= weekStart && date <= weekEndOf(weekStart));
};

/**
 * Applies `update` to schedule day `date` in the cached week holding it, building new objects so nothing cached is
 * changed in place: React Query hands out the cached objects themselves, so an in-place edit would corrupt the
 * cache without re-rendering. In-flight fetches of that week are cancelled first, so an older response can't land
 * over the patch.
 *
 * A week nobody has loaded is left alone: it comes from the server, with the change, when it's opened.
 */
export const patchScheduleDay = async (
	queryClient: QueryClient,
	departmentId: string,
	date: DateTime,
	update: (day: ScheduleDay) => ScheduleDay,
): Promise<void> => {
	const isoDate = toIsoDate(date);
	const holdingKeys = queryClient
		.getQueriesData<ScheduleWeek>({ queryKey: scheduleKeys.department(departmentId) })
		.flatMap(([queryKey, week]) => (week && weekHoldsAnyOf(queryKey, [isoDate]) ? [queryKey] : []));
	await Promise.all(holdingKeys.map((queryKey) => queryClient.cancelQueries({ queryKey, exact: true })));

	for (const queryKey of holdingKeys) {
		queryClient.setQueryData<ScheduleWeek>(
			queryKey,
			(week) => week && { ...week, days: week.days.map((day) => (toIsoDate(day.date) === isoDate ? update(day) : day)) },
		);
	}
};

/**
 * Ends a draft: empties every cached schedule week of the department, so no drafted edit can show again, and reloads
 * the weeks on screen; the others load when opened. Invalidating isn't enough here: a stale week keeps showing its
 * drafted copy until the reload lands, and keeps it if the reload fails. Wages aren't drafted, so they are only
 * marked stale.
 */
export const resetScheduleAfterDraft = async (queryClient: QueryClient, departmentId: string): Promise<void> => {
	await Promise.all([
		queryClient.resetQueries({ queryKey: scheduleKeys.department(departmentId) }),
		queryClient.invalidateQueries({ queryKey: scheduleWagesKeys.department(departmentId) }),
	]);
};

/**
 * Marks cached weeks stale, their wages too: only the weeks holding `dates`, or every week of the department when the
 * change's dates aren't known (settings, jobs, an HRIS sync). Weeks on screen refetch now; the others when opened. The
 * panels' per-person counts span every week, so any change marks them stale.
 */
export const invalidateSchedule = async (queryClient: QueryClient, departmentId: string, target: ScheduleInvalidation): Promise<void> => {
	const dates = "dates" in target ? target.dates.map(toIsoDate) : null;
	await Promise.all([
		...[scheduleKeys.department(departmentId), scheduleWagesKeys.department(departmentId)].map((queryKey) =>
			queryClient.invalidateQueries({ queryKey, predicate: (query) => dates === null || weekHoldsAnyOf(query.queryKey, dates) }),
		),
		queryClient.invalidateQueries({ queryKey: staffShiftStatsKeys.department(departmentId) }),
	]);
};
